<template>
  <div
    class="guide-intro"
    :style="{ '--intro-ms': `${INTRO_SPLASH_MS}ms` }"
    role="dialog"
    aria-modal="true"
    aria-label="כניסה למצב הדרכה"
    @click="$emit('continue')"
  >
    <div class="guide-intro__glow" aria-hidden="true"></div>

    <div class="guide-intro__content">
      <!-- Animated compass illustration -->
      <svg class="guide-intro__art" viewBox="0 0 120 120" aria-hidden="true">
        <!-- Outer ring -->
        <circle class="art-ring" cx="60" cy="60" r="46" />
        <!-- Tick marks (N/E/S/W) -->
        <g class="art-ticks">
          <line x1="60" y1="8" x2="60" y2="16" />
          <line x1="60" y1="104" x2="60" y2="112" />
          <line x1="8" y1="60" x2="16" y2="60" />
          <line x1="104" y1="60" x2="112" y2="60" />
        </g>
        <!-- Needle -->
        <g class="art-needle">
          <path d="M60 24 L69 60 L60 60 Z" fill="#fbbf24" />
          <path d="M60 24 L51 60 L60 60 Z" fill="#f59e0b" />
          <path d="M60 96 L69 60 L60 60 Z" fill="#e2e8f0" />
          <path d="M60 96 L51 60 L60 60 Z" fill="#cbd5e1" />
        </g>
        <circle class="art-hub" cx="60" cy="60" r="5" />
        <!-- Sparkles -->
        <g class="art-spark art-spark--1">
          <path d="M104 22 l2.2 5 5 2.2 -5 2.2 -2.2 5 -2.2 -5 -5 -2.2 5 -2.2 Z" />
        </g>
        <g class="art-spark art-spark--2">
          <path d="M16 88 l1.8 4 4 1.8 -4 1.8 -1.8 4 -1.8 -4 -4 -1.8 4 -1.8 Z" />
        </g>
        <g class="art-spark art-spark--3">
          <path d="M22 24 l1.5 3.4 3.4 1.5 -3.4 1.5 -1.5 3.4 -1.5 -3.4 -3.4 -1.5 3.4 -1.5 Z" />
        </g>
      </svg>

      <h2 class="guide-intro__title">נכנסים למצב הדרכה</h2>
      <p class="guide-intro__subtitle">נעבור יחד על הכפתורים החשובים במסך</p>
      <p class="guide-intro__hint">אפשר לצאת בכל שלב בלחיצה על ״דלג על הכל״</p>

      <!-- Auto-start progress -->
      <div class="guide-intro__progress" aria-hidden="true">
        <div class="guide-intro__progress-fill"></div>
      </div>
      <p class="guide-intro__tap">לחיצה על המסך מתחילה מיד</p>
    </div>

    <button type="button" class="guide-intro__skip" @click.stop="$emit('skipAll')">
      דלג על ההדרכה
    </button>
  </div>
</template>

<script setup>
import { INTRO_SPLASH_MS } from '../useTutorial'

defineEmits(['continue', 'skipAll'])
</script>

<style scoped>
.guide-intro {
  position: fixed;
  inset: 0;
  z-index: 10010;
  display: flex;
  align-items: center;
  justify-content: center;
  direction: rtl;
  font-family: var(--font-family);
  background: linear-gradient(160deg, #0f172a 0%, #1e293b 55%, #334155 100%);
  overflow: hidden;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.guide-intro__glow {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 45% at 50% 38%, rgba(59, 130, 246, 0.18), transparent 70%),
    radial-gradient(ellipse 40% 30% at 50% 42%, rgba(251, 191, 36, 0.08), transparent 70%);
  pointer-events: none;
}

.guide-intro__content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--spacing-lg);
  max-width: 420px;
  animation: introContentIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes introContentIn {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ---------- Illustration ---------- */
.guide-intro__art {
  width: clamp(96px, 22vw, 132px);
  height: clamp(96px, 22vw, 132px);
  margin-bottom: var(--spacing-lg);
}

.art-ring {
  fill: none;
  stroke: rgba(226, 232, 240, 0.85);
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-dasharray: 290;
  stroke-dashoffset: 290;
  animation: ringDraw 0.9s ease-out 0.1s forwards;
}

@keyframes ringDraw {
  to {
    stroke-dashoffset: 0;
  }
}

.art-ticks line {
  stroke: rgba(148, 163, 184, 0.9);
  stroke-width: 2.5;
  stroke-linecap: round;
}

.art-needle {
  transform-origin: 60px 60px;
  animation: needleSettle 1.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s both;
}

@keyframes needleSettle {
  0% {
    transform: rotate(-120deg);
  }
  60% {
    transform: rotate(18deg);
  }
  80% {
    transform: rotate(-8deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

.art-hub {
  fill: #f8fafc;
  stroke: #94a3b8;
  stroke-width: 2;
}

.art-spark {
  fill: #fbbf24;
  opacity: 0;
  animation: sparkTwinkle 1.4s ease-in-out infinite;
}

.art-spark--1 {
  animation-delay: 0.5s;
}

.art-spark--2 {
  animation-delay: 0.9s;
}

.art-spark--3 {
  animation-delay: 1.2s;
}

@keyframes sparkTwinkle {
  0%,
  100% {
    opacity: 0;
    transform: scale(0.6);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

.art-spark--1,
.art-spark--2,
.art-spark--3 {
  transform-box: fill-box;
  transform-origin: center;
}

/* ---------- Text ---------- */
.guide-intro__title {
  margin: 0 0 var(--spacing-sm);
  color: var(--color-white);
  font-size: clamp(1.4rem, 4.5vw, 1.75rem);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-tight);
}

.guide-intro__subtitle {
  margin: 0 0 var(--spacing-xs);
  color: var(--color-gray-300);
  font-size: clamp(0.95rem, 3vw, 1.05rem);
  line-height: var(--line-height-normal);
}

.guide-intro__hint {
  margin: 0 0 var(--spacing-lg);
  color: var(--color-gray-400);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
}

/* ---------- Progress ---------- */
.guide-intro__progress {
  width: min(220px, 60vw);
  height: 4px;
  border-radius: var(--border-radius-full);
  background: rgba(148, 163, 184, 0.25);
  overflow: hidden;
}

.guide-intro__progress-fill {
  height: 100%;
  width: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  animation: introProgress var(--intro-ms) linear forwards;
}

@keyframes introProgress {
  to {
    width: 100%;
  }
}

.guide-intro__tap {
  margin: var(--spacing-sm) 0 0;
  color: var(--color-gray-500);
  font-size: var(--font-size-xs);
}

/* ---------- Skip button ---------- */
.guide-intro__skip {
  position: absolute;
  top: calc(var(--spacing-md) + env(safe-area-inset-top, 0px));
  inset-inline-start: var(--spacing-md);
  padding: 10px 18px;
  min-height: 44px;
  border: 1px solid rgba(226, 232, 240, 0.35);
  border-radius: var(--border-radius-full);
  background: rgba(15, 23, 42, 0.4);
  color: var(--color-gray-200);
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.guide-intro__skip:hover {
  background: rgba(226, 232, 240, 0.12);
  border-color: rgba(226, 232, 240, 0.6);
  color: var(--color-white);
}

/* ---------- Reduced motion ---------- */
@media (prefers-reduced-motion: reduce) {
  .guide-intro__content,
  .art-ring,
  .art-needle,
  .art-spark {
    animation: none;
  }

  .art-ring {
    stroke-dashoffset: 0;
  }

  .art-spark {
    opacity: 1;
  }
}
</style>
